import classNames from "classnames";
import classes from "./FreeCapacityTile.module.less";
import { Card } from "~/features/configurator/Linear/components/Card";
import type { TrainDetails } from "~/features/freeCapacityPush/useFCCState";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { Trans, useTranslation } from "react-i18next";
import { Divider, Stack } from "~/common/mui";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { InfoCircle } from "~/common/components/InfoCircle/InfoCircle";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { Alert } from "~/common/mui/Alert/Alert";

interface Props {
  className?: string;
  data?: TrainDetails;
  trainLabel?: string;
  trainSelected?: boolean;
}

/*

 */

export const FreeCapacityTile = ({ data, className, trainLabel, trainSelected }: Props) => {
  const { t } = useTranslation();
  const trainId = trainLabel ?? t("page.fcc.freeCapacity.train");

  const hasTrain = !!data;

  const showDates = data && data.validFrom && data.validTo;
  const showAverageRlt = !!data?.avgRlt;
  const validQty = data?.avgPerMonthPcs && data.avgPerMonthPcs > 0;

  const amount = validQty ? NF.getDefaultFormatter().format(data.avgPerMonthPcs!) : "N/A";

  const popupContent = <Trans i18nKey={"page.fcc.freeCapacity.tooltip"} />;

  const isDesktop = useIsMinDesktopSize();
  return (
    <Card className={classNames(classes.root, className)}>
      <Stack gap={isDesktop ? 0 : 1} className={classes.titleSection}>
        <div className={classes.title}>
          <span>{t("page.fcc.freeCapacity.title")}</span>
        </div>
        <div className={classes.subtitle}>{trainId}</div>
      </Stack>

      {trainSelected && (
        <div className={classes.capacitySection}>
          <span className={classes.pieces}>{amount}</span>
          <span>{t("page.fcc.freeCapacity.pcs")}</span>
          <span>{t("page.fcc.freeCapacity.avg")}</span>
        </div>
      )}

      {(!isDesktop || (trainSelected && (showDates || showAverageRlt))) && (
        <Divider
          orientation={isDesktop ? "horizontal" : "vertical"}
          className={classes.divider}
          flexItem={!isDesktop}
        />
      )}

      {!trainSelected && <div className={classes.noTrain}>{t("page.fcc.freeCapacity.noTrain")}</div>}

      {trainSelected && (showDates || showAverageRlt) && (
        <div className={classes.contentSection}>
          <div className={classes.rltSection}>
            {showDates && (
              <>
                <label>{t("page.fcc.freeCapacity.valid")}</label>

                <Stack direction={"row"} gap={0.5} className={classes.rltValue}>
                  <em>{data?.validFrom?.format("MMM")}</em>
                  <span>{data?.validFrom?.format("YYYY")}</span>
                  <Icon type={IconType.ArrowLeft} size={12} />
                  <em>{data?.validTo?.format("MMM")}</em>
                  <span>{data?.validTo?.format("YYYY")}</span>
                </Stack>
              </>
            )}
            {showAverageRlt && (
              <>
                <label>{t("page.fcc.freeCapacity.averageRlt")}</label>

                <Stack direction={"row"} gap={0.5} className={classes.rltValue}>
                  <em>{NF.getDefaultFormatter().format(data!.avgRlt!)}</em>
                  <span>{t("page.fcc.freeCapacity.day", { count: data!.avgRlt! })}</span>
                </Stack>
              </>
            )}
          </div>
        </div>
      )}

      {hasTrain && !validQty && (
        <Alert severity={"info"} className={classes.info}>
          {t("page.fcc.freeCapacity.noCapacity")}
        </Alert>
      )}

      <InfoCircle
        content={popupContent}
        tooltipClassName={classes.tooltipContent}
        placement={"left"}
        className={classes.infoIcon}
      />
    </Card>
  );
};
